import React, { useState, useEffect } from 'react';
import { Wordmark } from './BrandMark';
import { track } from '../analytics';
import './UserSetup.css';

const AVATARS = ['👤', '🧑‍💻', '👩‍💼', '🧑‍🔬', '👨‍🎨', '🧑‍🏫', '👩‍🚀', '🦊', '🐔', '🐧'];

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

// Pull a one-time ?reset-token=… off the URL and strip it so a refresh
// doesn't re-open the reset form. Returns null when absent.
function readResetTokenFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset-token');
    if (token) {
      const url = new URL(window.location.href);
      url.searchParams.delete('reset-token');
      window.history.replaceState({}, '', url.toString());
    }
    return token || null;
  } catch {
    return null;
  }
}

// Pull a one-time ?verify-email=… off the URL (the link from the verification
// email) and strip it so a refresh doesn't re-submit it. Returns null when absent.
function readVerifyTokenFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('verify-email');
    if (token) {
      const url = new URL(window.location.href);
      url.searchParams.delete('verify-email');
      window.history.replaceState({}, '', url.toString());
    }
    return token || null;
  } catch {
    return null;
  }
}

// Read the ?invite=CODE param without stripping it — App.js still redeems it
// after auth on open deployments, so this must stay non-destructive. On an
// invite-only deployment the code is what unlocks the Register tab, and the
// register call consumes it (the server answers `inviteAccepted`, and we strip
// the param then so a refresh doesn't try to redeem it twice).
function readInviteCodeFromUrl() {
  try {
    return new URLSearchParams(window.location.search).get('invite') || null;
  } catch {
    return null;
  }
}

function stripInviteCodeFromUrl() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete('invite');
    window.history.replaceState({}, '', url.toString());
  } catch {
    /* non-fatal — the worst case is a redundant redeem attempt */
  }
}

// Pick the tab to open from the entry URL: the marketing site's "Start free"
// CTAs point at /register (or ?mode=register), while "Sign in" points at /login.
// The app is a SPA served for every path, so these URLs already load — this just
// opens the matching tab instead of always landing on login. Verify/reset links
// (handled in the effect below) still take priority over this.
function readInitialAuthMode() {
  try {
    const params = new URLSearchParams(window.location.search);
    const queryMode = params.get('mode');
    if (queryMode === 'register' || queryMode === 'login') return queryMode;
    const path = window.location.pathname.replace(/\/+$/, '');
    if (path === '/register' || path === '/signup') return 'register';
    return 'login';
  } catch {
    return 'login';
  }
}

function UserSetup({ onSetup }) {
  // 'login' | 'register' | 'forgot' | 'reset' | 'verify-sent' | 'verifying'
  const [mode, setMode] = useState(readInitialAuthMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [avatar, setAvatar] = useState(AVATARS[0]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  // An invite code on the entry URL (?invite=CODE), captured once on mount so
  // it survives the redeem-and-strip below.
  const [inviteCode] = useState(readInviteCodeFromUrl);
  // Whether this deployment takes public signups. Starts false so the Register
  // tab isn't offered for the split second before /api/auth/config answers on
  // a locked-down deployment; `configLoaded` keeps the "invite-only" copy from
  // flashing on an open one.
  const [inviteOnly, setInviteOnly] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  // "Not ready for an account?" email capture (gtm-playbook.md §8c #3).
  const [leadEmail, setLeadEmail] = useState('');
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [leadError, setLeadError] = useState('');
  const [leadBusy, setLeadBusy] = useState(false);
  // Registration is on offer when the deployment is open, or when it's
  // invite-only and the visitor arrived through an invite link.
  const canRegister = !inviteOnly || !!inviteCode;

  // Ask the server whether signups are open. A failure leaves the default
  // (open) in place — the register endpoint is the real gate either way.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${SERVER_URL}/api/auth/config`);
        const data = await res.json();
        if (!cancelled && res.ok) setInviteOnly(!!data.inviteOnly);
      } catch {
        /* leave the default */
      }
      if (!cancelled) setConfigLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // If signups turn out to be closed and we're sitting on the register tab
  // without an invite, fall back to log in rather than showing a dead form.
  useEffect(() => {
    if (configLoaded && !canRegister && mode === 'register') setMode('login');
  }, [configLoaded, canRegister, mode]);

  // The top of the funnel (gtm-playbook.md §8c #2). `signup` fires only on a
  // completed account, so without this every pre-account drop-off — never
  // reached the app, reached it and bounced off the form — is invisible and the
  // §8a hypotheses can't be told apart. Fired once per mount of the auth screen.
  useEffect(() => {
    track('auth-screen-viewed', { mode: readInitialAuthMode() });
  }, []);

  // If the page was opened from an email link, act on it on first render: a
  // verification link logs the user straight in; a reset link opens the reset
  // form. (Verification takes priority — they're mutually exclusive in practice.)
  useEffect(() => {
    const verifyTok = readVerifyTokenFromUrl();
    if (verifyTok) {
      handleVerifyEmail(verifyTok);
      return;
    }
    const token = readResetTokenFromUrl();
    if (token) {
      setResetToken(token);
      setMode('reset');
    }
  }, []);

  // Redeem a verification token: on success the account is verified and we get
  // a session straight back, so we log the user in. On failure we surface the
  // reason and offer a resend.
  const handleVerifyEmail = async (token) => {
    setMode('verifying');
    setError('');
    setNotice('');
    try {
      const res = await fetch(`${SERVER_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'This verification link is invalid or has expired.');
        setMode('login');
        return;
      }
      track('verification-completed');
      localStorage.setItem('symphonate-token', data.token);
      localStorage.setItem('symphonate-user', JSON.stringify(data.user));
      onSetup({ ...data.user, token: data.token, workspaces: data.workspaces || [] }, 'login');
    } catch {
      setError('Could not connect to server to verify your email.');
      setMode('login');
    }
  };

  // Resend the verification email. The server responds generically (no account
  // enumeration), so we show its message verbatim.
  const handleResendVerification = async () => {
    setError('');
    setNotice('');
    setLoading(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      setNotice(data.message || 'If your account still needs verifying, a new link is on its way.');
    } catch {
      setError('Could not connect to server');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // The form was actually submitted — pairs with `auth-screen-viewed` above
    // and `signup` on success, so the register-form drop-off rate is readable
    // instead of inferred (gtm-playbook.md §8c #2).
    if (mode === 'register') track('register-submitted', { invited: !!inviteCode });

    try {
      const endpoint = mode === 'register' ? '/api/auth/register' : '/api/auth/login';
      const body =
        mode === 'register'
          ? {
              email: email.trim(),
              password,
              name: name.trim(),
              avatar,
              termsAccepted,
              // Proof of invitation on an invite-only deployment; ignored by an
              // open one, which still redeems the code after auth in App.js.
              inviteCode: inviteCode || undefined,
            }
          : { email: email.trim(), password };

      const res = await fetch(`${SERVER_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) {
        // A deployment still running the old server gates sign-in on
        // verification. Route those users to the "check your inbox" panel
        // (with a resend) instead of a dead-end error. The current server never
        // answers this — verification stopped gating the front door in §8c #1.
        if (data.code === 'email_unverified') {
          setError('');
          setNotice(data.error || 'Please verify your email before signing in.');
          track('verification-required-shown', { at: mode });
          setMode('verify-sent');
          setLoading(false);
          return;
        }
        setError(data.error || 'Something went wrong');
        setLoading(false);
        return;
      }

      // When the server consumed the invite as part of registration, the new
      // account is already in the workspace — drop the code so App.js doesn't
      // try to redeem an invite that's now marked accepted.
      if (data.inviteAccepted) stripInviteCodeFromUrl();

      // Old-server compatibility: a registration that withholds the session
      // token until the emailed link is clicked. The current server always
      // issues one and falls through to the sign-in below (§8c #1).
      if (data.verificationRequired) {
        setNotice(
          data.message ||
            "Almost there! We've sent a verification link to your email. Click it to finish setting up your account.",
        );
        track('verification-required-shown', { at: 'register' });
        setMode('verify-sent');
        setLoading(false);
        return;
      }

      // Store token for session persistence
      localStorage.setItem('symphonate-token', data.token);
      localStorage.setItem('symphonate-user', JSON.stringify(data.user));

      onSetup({ ...data.user, token: data.token, workspaces: data.workspaces || [] }, mode);
    } catch {
      setError('Could not connect to server');
    }
    setLoading(false);
  };

  // Leave an address without creating an account (gtm-playbook.md §8c #3). The
  // in-app fake door only reaches people who already signed up, so a visitor
  // who isn't ready has otherwise no way to raise a hand — and the funnel can't
  // tell "not interested" from "not yet".
  const handleLeaveEmail = async (e) => {
    e.preventDefault();
    setLeadError('');
    setLeadBusy(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: leadEmail.trim(), source: 'auth-screen' }),
      });
      const data = await res.json();
      if (!res.ok) {
        setLeadError(data.error || 'Could not save your email');
      } else {
        setLeadSent(true);
        track('lead-captured', { source: 'auth-screen' });
      }
    } catch {
      setLeadError('Could not connect to server');
    }
    setLeadBusy(false);
  };

  // Request a reset link. The server always responds with the same generic
  // message (no account enumeration), so we show it verbatim.
  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong');
      } else {
        setNotice(
          data.message || 'If an account exists for that email, a reset link is on its way.',
        );
      }
    } catch {
      setError('Could not connect to server');
    }
    setLoading(false);
  };

  // Submit a new password using the emailed token.
  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not reset password');
      } else {
        setNotice(data.message || 'Your password has been reset. You can now log in.');
        setMode('login');
        setPassword('');
        setConfirmPassword('');
        setResetToken(null);
      }
    } catch {
      setError('Could not connect to server');
    }
    setLoading(false);
  };

  return (
    <div className="user-setup">
      <div className="setup-card">
        <div className="setup-header">
          <h1>
            <Wordmark size="2.8rem" withMark beta />
          </h1>
          <p>Real-time collaboration between your team and AI agents.</p>
        </div>

        {(mode === 'login' || mode === 'register') && (
          <>
            <div className="auth-tabs">
              <button
                className={`auth-tab ${mode === 'login' ? 'active' : ''}`}
                onClick={() => {
                  setMode('login');
                  setError('');
                  setNotice('');
                }}
              >
                Log In
              </button>
              {/* Closed signups: no Register tab unless an invite link brought
                  them here. The server enforces the same rule. */}
              {canRegister && (
                <button
                  className={`auth-tab ${mode === 'register' ? 'active' : ''}`}
                  onClick={() => {
                    setMode('register');
                    setError('');
                    setNotice('');
                  }}
                >
                  Register
                </button>
              )}
            </div>

            {inviteOnly && inviteCode && (
              <p className="setup-blurb">
                You&apos;ve been invited. Create an account to join, or log in if you already have
                one.
              </p>
            )}
            {configLoaded && !canRegister && (
              <p className="setup-blurb">
                This Symphonate deployment is invite-only. Ask a member for an invite link to get an
                account.
              </p>
            )}

            <form onSubmit={handleSubmit}>
              {mode === 'register' && (
                <div className="form-group">
                  <label>Your Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your name..."
                    maxLength={30}
                  />
                </div>
              )}

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError('');
                  }}
                  placeholder="Enter your email..."
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  placeholder={
                    mode === 'register'
                      ? 'Choose a password (6+ chars)...'
                      : 'Enter your password...'
                  }
                />
                {error && <span className="error-text">{error}</span>}
                {notice && <span className="notice-text">{notice}</span>}
              </div>

              {mode === 'register' && (
                <div className="form-group">
                  <label>Avatar</label>
                  <div className="avatar-grid">
                    {AVATARS.map((a) => (
                      <button
                        key={a}
                        type="button"
                        className={`avatar-btn ${avatar === a ? 'selected' : ''}`}
                        onClick={() => setAvatar(a)}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {mode === 'register' && (
                <label className="terms-check">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                  />
                  <span>
                    I confirm I&apos;m 18 or older and agree to the{' '}
                    <a href="https://symphonate.ai/terms" target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://symphonate.ai/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                    .
                  </span>
                </label>
              )}

              <button
                type="submit"
                className="btn-primary"
                disabled={
                  loading ||
                  !email.trim() ||
                  !password ||
                  (mode === 'register' && (!name.trim() || !termsAccepted))
                }
              >
                {loading ? 'Please wait...' : mode === 'register' ? 'Create Account' : 'Log In'}
              </button>
            </form>

            {mode === 'login' && (
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setMode('forgot');
                  setError('');
                  setNotice('');
                }}
              >
                Forgot your password?
              </button>
            )}

            {/* Email capture for visitors who won't create an account today. */}
            {leadSent ? (
              <p className="lead-capture-done">Thanks — we&apos;ll be in touch.</p>
            ) : leadOpen ? (
              <form className="lead-capture" onSubmit={handleLeaveEmail}>
                <label htmlFor="lead-email">
                  Leave your email and we&apos;ll let you know when it&apos;s a fit.
                </label>
                <div className="lead-capture-row">
                  <input
                    id="lead-email"
                    type="email"
                    value={leadEmail}
                    onChange={(e) => {
                      setLeadEmail(e.target.value);
                      setLeadError('');
                    }}
                    placeholder="you@company.com"
                  />
                  <button
                    type="submit"
                    className="btn-secondary"
                    disabled={leadBusy || !leadEmail.trim()}
                  >
                    {leadBusy ? 'Sending...' : 'Send'}
                  </button>
                </div>
                {leadError && <span className="error-text">{leadError}</span>}
              </form>
            ) : (
              <button type="button" className="link-btn" onClick={() => setLeadOpen(true)}>
                Not ready for an account?
              </button>
            )}
          </>
        )}

        {/* --- Verifying an email link (in flight) --- */}
        {mode === 'verifying' && (
          <div className="setup-blurb" style={{ textAlign: 'center' }}>
            <p>Verifying your email…</p>
          </div>
        )}

        {/* --- Check your inbox (post-register / unverified login) --- */}
        {mode === 'verify-sent' && (
          <div className="verify-sent">
            <p className="setup-blurb">
              {notice ||
                "We've sent a verification link to your email. Click it to finish setting up your account."}
            </p>
            {error && <span className="error-text">{error}</span>}
            <button
              type="button"
              className="btn-primary"
              disabled={loading || !email.trim()}
              onClick={handleResendVerification}
            >
              {loading ? 'Please wait...' : 'Resend verification email'}
            </button>
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                setMode('login');
                setError('');
                setNotice('');
              }}
            >
              Back to log in
            </button>
          </div>
        )}

        {/* --- Forgot password (request a link) --- */}
        {mode === 'forgot' && (
          <form onSubmit={handleForgot}>
            <p className="setup-blurb">
              Enter your email and we&apos;ll send you a link to reset your password.
            </p>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                placeholder="Enter your email..."
                autoFocus
              />
              {error && <span className="error-text">{error}</span>}
              {notice && <span className="notice-text">{notice}</span>}
            </div>

            <button type="submit" className="btn-primary" disabled={loading || !email.trim()}>
              {loading ? 'Sending...' : 'Send reset link'}
            </button>
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                setMode('login');
                setError('');
                setNotice('');
              }}
            >
              Back to log in
            </button>
          </form>
        )}

        {/* --- Reset password (set a new one via emailed token) --- */}
        {mode === 'reset' && (
          <form onSubmit={handleReset}>
            <p className="setup-blurb">Choose a new password for your account.</p>
            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                placeholder="Choose a password (6+ chars)..."
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setError('');
                }}
                placeholder="Re-enter your new password..."
              />
              {error && <span className="error-text">{error}</span>}
              {notice && <span className="notice-text">{notice}</span>}
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !password || !confirmPassword}
            >
              {loading ? 'Resetting...' : 'Reset password'}
            </button>
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                setMode('login');
                setError('');
                setNotice('');
              }}
            >
              Back to log in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default UserSetup;
